/**
 * formFieldTypes.ts — shared typed bags for form field/config JSON.
 *
 * The builder and runtime historically typed field.options, field.validation,
 * field.conditional_logic, formData, and the form-level config bags as
 * `Record<string, any>` (or bare `any`), which disabled all type checking on
 * the most-edited surfaces of the forms system. These types keep the loose,
 * forward-compatible shape (index signatures over `unknown`) while giving the
 * commonly-read keys real types — so existing reads like
 * `field.options?.choices` keep working but misspelled/misused keys now fail
 * type-check instead of silently passing as `any`.
 */

import type { FormFieldOption } from './formConfigurationUtils';

// ── Generic JSON helpers ────────────────────────────────────────

/** A parsed-JSON object with unknown values (replaces Record<string, any>). */
export type JsonObject = Record<string, unknown>;

/** Form response / working data map — values are heterogeneous per field type. */
export type FormValues = Record<string, unknown>;

/** Narrow an unknown JSON value to a record (returns null otherwise). */
export function asObject(value: unknown): JsonObject | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as JsonObject)
    : null;
}

/** Narrow an unknown JSON value to an array (returns [] otherwise). */
export function asArray<T = unknown>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

/** Coerce an unknown JSON value to a trimmed string (or fallback). */
export function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : value == null ? fallback : String(value);
}

/** Coerce an unknown JSON value to a finite number (or fallback). */
export function asNumber(value: unknown, fallback = 0): number {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
}

/** Coerce an unknown JSON value to boolean. */
export function asBoolean(value: unknown, fallback = false): boolean {
  return typeof value === 'boolean' ? value : value == null ? fallback : Boolean(value);
}

// ── Runtime field values ────────────────────────────────────────

/**
 * Runtime value a single form field can hold. Covers primitives (text/number/
 * toggle), arrays (checkboxes, tags, user multi-select, photo lists, module
 * role assignments) and structured objects (pass_fail results, measurement
 * {value,unit}, file uploads {file_url,...}, geofence assignments…).
 *
 * Replaces `value: any` on field component props: components narrow via
 * typeof/Array.isArray/asObject before use.
 */
export type FieldValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | Record<string, unknown>
  | unknown[];

/** formData bag — field key → value. */
export type FormDataValues = Record<string, FieldValue>;

/**
 * Field descriptor as consumed by the presentational field components under
 * `fields/`. Bags stay `unknown`-valued — each component narrows `metadata`/
 * `validation` via a local cast for the keys it reads. All prop types are
 * `unknown`-compatible so both the builder and runtime `FormField` shapes
 * (which carry index signatures) remain assignable.
 */
export interface FieldComponentField {
  key?: string;
  type?: string;
  label?: string;
  placeholder?: string;
  help_text?: string;
  helpText?: string;
  required?: boolean;
  readonly?: boolean;
  is_readonly?: boolean;
  default_value?: unknown;
  defaultValue?: unknown;
  options?: unknown;
  validation?: unknown;
  metadata?: Record<string, unknown>;
  styling?: unknown;
  [key: string]: unknown;
}

/** Normalize an unknown options payload to `FormFieldOption[]`. */
export function asOptionArray(value: unknown): FormFieldOption[] {
  if (!Array.isArray(value)) return [];
  const out: FormFieldOption[] = [];
  for (const entry of value) {
    if (typeof entry === 'string' || typeof entry === 'number') {
      const s = String(entry);
      out.push({ value: s, label: s });
      continue;
    }
    const rec = asObject(entry);
    if (!rec) continue;
    const rawValue = rec.value;
    const valueStr = typeof rawValue === 'string' || typeof rawValue === 'number'
      ? String(rawValue)
      : null;
    if (!valueStr) continue;
    out.push({
      ...rec,
      value: valueStr,
      label: typeof rec.label === 'string' && rec.label ? rec.label : valueStr,
    });
  }
  return out;
}

/** Coerce a FieldValue to a controlled <input> value (string | number). */
export function asInputValue(value: FieldValue): string | number {
  if (typeof value === 'number') return Number.isFinite(value) ? value : '';
  if (typeof value === 'string') return value;
  if (typeof value === 'boolean') return String(value);
  return '';
}

/** Coerce a FieldValue to string[] (drops non-string members). */
export function asStringArray(value: FieldValue): string[] {
  if (Array.isArray(value)) return value.filter((v): v is string => typeof v === 'string');
  return typeof value === 'string' && value ? [value] : [];
}

// ── Field-level bags ────────────────────────────────────────────

/**
 * Field `options` bag — heterogeneous per field type, so the index signature
 * stays `unknown`, but the keys every editor/renderer reads are declared.
 */
export interface FieldOptions {
  // Choice lists (all shapes seen in the wild; readers normalize strings).
  choices?: FormFieldOption[];
  items?: FormFieldOption[];
  options?: Array<FormFieldOption | string>;
  ranking_items?: string[];
  allow_other?: boolean;

  // Numeric-ish constraints shared by number/measurement/rating/scale.
  min?: number;
  max?: number;
  precision?: number;
  tolerance?: number;
  low_label?: string;
  high_label?: string;
  scale_type?: string;
  show_gauge?: boolean;
  show_value?: boolean;

  // Measurement / pass_fail extras.
  unit?: string;
  allow_na?: boolean;
  require_comment_on_fail?: boolean;
  require_photo_on_fail?: boolean;

  // Matrix.
  rows?: string[];
  columns?: string[];

  // File / photo / signature capture.
  allowed_types?: string[];
  formats?: string[];
  max_files?: number;
  max_file_size?: number;
  max_size_mb?: number;
  min_photos?: number;
  max_photos?: number;
  quality?: number;
  require_preview?: boolean;
  require_alt_text?: boolean;
  photo_instructions?: string;
  signer_role?: string;
  require_name?: boolean;
  require_full_name?: boolean;
  require_id_number?: boolean;
  require_designation?: boolean;
  require_gps?: boolean;
  require_accuracy?: number | boolean;
  require_timestamp?: boolean;
  show_map?: boolean;

  // Dynamic select / lookup / data source.
  lookup_endpoint?: string;
  value_field?: string;
  label_field?: string;
  data_source_type?: string;
  data_source_url?: string;

  // OTP / signature extras.
  delivery_method?: string;
  code_length?: number;
  expiry_seconds?: number;
  consent_text?: string;

  // Quiz / scoring.
  scoring?: boolean;
  correct_answer?: unknown;

  // Display / misc.
  image_url?: string;
  placeholder?: string;
  max_selections?: number;
  max_length?: number;
  resend_allowed?: boolean;
  ttl_seconds?: number;

  /** Forward-compatible bag for type-specific extras. */
  [key: string]: unknown;
}

/**
 * Field `validation` bag — declared keys cover every key read today;
 * the index signature keeps room for custom validators.
 */
export interface FieldValidation {
  min?: number;
  max?: number;
  min_value?: number;
  max_value?: number;
  minLength?: number;
  maxLength?: number;
  min_length?: number;
  max_length?: number;
  pattern?: string;
  regex?: string;
  tolerance?: number;
  required?: boolean;
  custom_message?: string;
  [key: string]: unknown;
}

/**
 * Field/section `conditional_logic` bag — supports both camelCase
 * (builder) and snake_case (runtime/AI) condition keys.
 */
export interface ConditionalLogic {
  dependsOn?: string;
  depends_on?: string;
  operator?: string;
  value?: unknown;
  /** Builder shorthand: show this field when dependsOn equals this value. */
  show_when?: string;
  conditions?: Array<{
    field?: string;
    fieldKey?: string;
    operator?: string;
    value?: unknown;
  }>;
  action?: string;
  [key: string]: unknown;
}
